import React, { CSSProperties, useEffect, useRef, useState } from "react";
import axios from "axios";
import styles from './SearchBar.module.css'
import { cn } from "../../utils/cn.ts";
import { Button } from "../../ui/General/Button/Button.tsx";
import { Icon } from "../../ui/General/Icon/Icon.tsx";
import { arrowDownIcon } from "../../assets/icons/icons.ts";
import { LinearLoader } from "../../ui/LinearLoader/LinearLoader.tsx";
import { useScreenDetector } from "../../hooks/useScreenDetector.ts";
import { API_URL } from "../../config.tsx";

interface Props {
    placeholder?: string
    isMobile?: boolean
}

interface PropsSelect {
    options: {
        id: number,
        value: string,
        brand_slug: string,
        brand: string,
        article: string,
        img: string,
    }[],
    value: string,
    onChange: (value: string) => void,
    openSearch: () => void,
    onSelect: (value: any) => void,
    onSelectOption: (value: any) => void,
    placeholder?: string,
    shownArrow?: boolean,
    isLoading?: boolean,
    shownSearchIcon?: boolean,
    isOpened: boolean,
    rootClassName?: string,
    selectClassName?: string,
    inputClassName?: string,
    styles?: string | CSSProperties
}

const Select: React.FC<PropsSelect> = ({
    rootClassName,
    selectClassName,
    inputClassName,
    options,
    value,
    onChange,
    onSelect,
    onSelectOption,
    openSearch,
    shownArrow = true,
    isLoading = true,
    shownSearchIcon = false,
    isOpened = false,
    placeholder = '---',
}) => {
    const { isDesktop, isMobile } = useScreenDetector()
    const [isOpen, setIsOpen] = useState(isOpened);
    const [searchFieldIsOnFocus, setSearchFieldIsOnFocus] = useState(false);
    const [isLoad, setIsLoad] = useState(isLoading);
    const [optionsList, setOptionsList] = useState<{
        id: number,
        value: string,
        brand_slug: string,
        brand: string,
        article: string,
        img: string,
    }[]>(options);
    const [searchValue, setSearchValue] = useState(value);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const windowWidth = document.body.clientWidth;

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        setOptionsList(options);
    }, [options]);

    useEffect(() => {
        setSearchValue(value);
        options.map(option => {
            if (
                option.value.toLowerCase() === value.toLowerCase()
                || option.value.replace(' ', '').toLowerCase() === value.toLowerCase()
                || option.value.replace('-', '_').toLowerCase() === value.toLowerCase()
            ) {
                handleOptionSelect(option.value);
                onSelectOption(option);
            }
        })
    }, [value]);

    useEffect(() => {
        setIsLoad(isLoading);
    }, [isLoading]);

    useEffect(() => {
        setIsOpen(isOpened);
    }, [isOpened]);

    function handleToggleDropdown(): void {
        inputRef.current?.focus();

        setIsOpen(!isOpen);
    }

    function handleOptionSelect(selectedValue: string): void {
        onSelect(selectedValue);
        setIsOpen(false);
        setSearchValue(selectedValue);
    }

    function handleClickOutside(event: MouseEvent): void {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            // setSearchValue(searchValue || '');
        }
    }

    function handleSearchChange(value: string): void {
        setSearchValue(value);
        onChange(value);
    }

    return (
        <div ref={containerRef} className={cn(styles.container, rootClassName)}>
            <div className={cn(styles.select, selectClassName, isOpen && styles.isOpen)} onClick={handleToggleDropdown}>
                {shownSearchIcon && <Icon name='search' width={18} height={18} color='#fff' />}
                <input
                    type='text'
                    placeholder={placeholder}
                    className={cn(styles.searchInput, isOpen && styles.isOpen, inputClassName)}
                    ref={inputRef}
                    value={searchValue}
                    onChange={event => handleSearchChange(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            openSearch();
                        }
                    }}
                    onFocus={() => setSearchFieldIsOnFocus(true)}
                    onBlur={() => setSearchFieldIsOnFocus(false)}
                />
                {shownArrow && (<div
                    className={cn(
                        styles.arrow,
                        isOpen && styles.arrowOpen
                    )}
                >
                    <img width={14} height={16} src={arrowDownIcon} alt='Стрелка вниз' />
                </div>)}
            </div>

            {(isOpen || searchFieldIsOnFocus) && optionsList.length && isDesktop ?
                (
                    <ul className={styles.dropdown} style={{ maxHeight: 500, overflowY: 'auto', }}>
                        {optionsList.map((option) => (
                            <li
                                key={option.id}
                                onClick={() => {
                                    handleOptionSelect(option.value);
                                    onSelectOption(option);
                                }}
                                className={cn(
                                    styles.option,
                                    value === option.value && styles.optionSelected,
                                )}
                            >
                                <div className={styles.row}>
                                    <div>
                                        <img width={48} height={48} src={option.img} alt={option.value} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', }}>{option.value}</div>
                                        <div style={{ display: 'inline-flex', }}>
                                            <div className={styles.rowItem}>{option.brand}</div>
                                            <div className={styles.rowItem} style={{ marginLeft: 8, }}>{option.article}</div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : null
            }

            {isOpen && optionsList.length && isMobile ?
                (
                    <div className={styles.dropdown} style={{ width: windowWidth, left: -15, maxHeight: 500, overflowY: 'auto', borderRadius: 0, }}>
                        {optionsList.map((option) => (
                            <div
                                key={option.id}
                                onClick={() => {
                                    handleOptionSelect(option.value);
                                    onSelectOption(option);
                                }}
                                className={cn(
                                    styles.option,
                                    value === option.value && styles.optionSelected,
                                )}
                            >
                                <div className={styles.row}>
                                    <div>
                                        <img width={48} height={48} src={option.img} alt={option.value} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', }}>{option.value}</div>
                                        <div style={{ display: 'inline-flex', }}>
                                            <div className={styles.rowItem}>{option.brand}</div>
                                            <div className={styles.rowItem} style={{ marginLeft: 8, }}>{option.article}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null
            }
            {
                isLoad && !isOpen && !optionsList.length
                    ? <div className={styles.dropdown}><LinearLoader /></div>
                    : null
            }
        </div>
    );
};

export const SearchBar: React.FC<Props & React.HTMLAttributes<HTMLButtonElement>> = ({
    className,
    isMobile = false,
    placeholder = 'Поиск'
}) => {
    const url = new URL(window?.location.href || '');
    const queryParam = url.searchParams.get('query');
    const [searchResult, setSearchResult] = useState(queryParam || '');
    const [brandSlug, setBrandSlug] = useState('');
    const [article, setArticle] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    // const [controllers, setControllers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const isDesktop = !isMobile;

    //Дебаунс поискового запроса
    useEffect(() => {
        let timer: NodeJS.Timeout;

        timer = setTimeout(() => {
            if (searchResult) {
                setIsLoading(true);
                axios.get(`${API_URL}/find_products?query=${searchResult}`, { withCredentials: true })
                    .then(response => response.data)
                    .then(products => {
                        setSearchResults(products)
                        if (products.length) setIsOpened(true);
                    })
                    .finally(() => setIsLoading(false));
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchResult]);

    const changeSearchResult = (searchQuery: any) => {
        // setSearchResults([])
        // setIsLoading(true);
        // handleAbort();
        // let newControllersList = controllers;
        // const abortController = new AbortController();
        // // @ts-ignore
        // newControllersList.push(abortController);
        // setControllers(newControllersList);
        setSearchResult(searchQuery);
        // fetch(`${API_URL}/find_products?query=${searchQuery}`, {
        //     method: 'GET',
        //     credentials: 'include',
        //     signal: abortController.signal
        // })
        // .then(response => response.json())
        // .then(products => {
        //     setSearchResults(products)
        //     setIsLoading(false);
        //     if(products.length) setIsOpened(true);
        // })
    }

    // const handleAbort = () => {
    //     controllers.map(controller => {
    //         // @ts-ignore
    //         controller.abort();
    //     })
    // }

    const selectSearchResult = (product: { id: number, brand_slug: string, article: string }) => {
        setBrandSlug(product.brand_slug);
        setArticle(product.article);
        openProductCard(product.brand_slug, product.article);
    }

    const openProductCard = (brandSlug: string, article: string) => {
        if (brandSlug && article) document.location = `/spares/${brandSlug}/${article}`;
        else if (searchResult) document.location = `/search?query=${searchResult}`;
    }

    const openSearch = () => {
        if (brandSlug && article) document.location = `/spares/${brandSlug}/${article}`;
        else if (searchResult) document.location = `/search?query=${searchResult}`;
    }

    return (
        <>
            {isDesktop && (<div className={cn(styles.container, className)}>
                <Select
                    shownSearchIcon={true}
                    shownArrow={false}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    options={searchResults}
                    value={searchResult ?? ''}
                    onChange={changeSearchResult}
                    onSelect={selectSearchResult}
                    onSelectOption={selectSearchResult}
                    isOpened={isOpened}
                    openSearch={openSearch}
                />
                <Button type='search' onClick={() => openProductCard(brandSlug, article)} id='search-btn' />
            </div>)}

            {isMobile && (<>
                <Select
                    shownSearchIcon={false}
                    shownArrow={false}
                    placeholder={placeholder}
                    isLoading={isLoading}
                    options={searchResults}
                    value={searchResult ?? ''}
                    onChange={changeSearchResult}
                    onSelect={selectSearchResult}
                    onSelectOption={selectSearchResult}
                    isOpened={isOpened}
                    openSearch={openSearch}
                />
                <Button type='search' onClick={() => openProductCard(brandSlug, article)} />
            </>)}
        </>
    )
}
